import {
  compareServiceWorkItems,
  type ServiceRequestReadModel,
  type ServiceWorkItemReadModel,
} from "@billiard/contracts";

type ServiceRequestSource = Extract<
  ServiceWorkItemReadModel["source"],
  { kind: "SERVICE_REQUEST" }
>;

export type ServiceRequestWorkItemReadModel = Omit<ServiceWorkItemReadModel, "source"> & {
  source: ServiceRequestSource;
};

export interface ServiceRequestStaffRow {
  request: ServiceRequestReadModel;
  workItem: ServiceRequestWorkItemReadModel | null;
}

function isMatchingServiceRequestWorkItem(
  item: ServiceWorkItemReadModel,
  request: ServiceRequestReadModel,
): item is ServiceRequestWorkItemReadModel {
  return (
    item.source.kind === "SERVICE_REQUEST" &&
    item.source.eventId === request.eventId &&
    item.source.type === request.type &&
    item.source.state === request.status &&
    item.requestedAt === request.requestedAt &&
    item.assigneeDisplayName === request.assignedActorDisplayName &&
    item.assignedToCurrentActor === request.assignedToCurrentActor
  );
}

function matchingWorkItem(
  request: ServiceRequestReadModel,
  workItems: readonly ServiceWorkItemReadModel[],
  workItemsTrusted: boolean,
): ServiceRequestWorkItemReadModel | null {
  if (!workItemsTrusted) return null;
  const matches = workItems.filter((item) => isMatchingServiceRequestWorkItem(item, request));
  return matches.length === 1 ? (matches[0] ?? null) : null;
}

export function serviceRequestStaffRows(
  requests: readonly ServiceRequestReadModel[],
  workItems: readonly ServiceWorkItemReadModel[],
  workItemsTrusted: boolean,
  focusedEventId: string | null,
): ServiceRequestStaffRow[] {
  return requests
    .map((request) => ({
      request,
      workItem: matchingWorkItem(request, workItems, workItemsTrusted),
    }))
    .sort((left, right) => {
      const leftFocused = left.request.eventId === focusedEventId;
      const rightFocused = right.request.eventId === focusedEventId;
      if (leftFocused !== rightFocused) return leftFocused ? -1 : 1;

      if (left.workItem && right.workItem) {
        return compareServiceWorkItems(left.workItem, right.workItem);
      } else if (left.workItem !== right.workItem) {
        return left.workItem ? -1 : 1;
      }

      const requestedDifference = left.request.requestedAt.localeCompare(right.request.requestedAt);
      return requestedDifference !== 0
        ? requestedDifference
        : left.request.eventId.localeCompare(right.request.eventId);
    });
}

function formatRequestTime(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

export function serviceRequestDeadlineLabel(item: ServiceRequestWorkItemReadModel): string {
  if (item.dueAt === null) return "当前未设置处理时限";
  const dueAt = formatRequestTime(item.dueAt);
  if (item.slaStatus === "OVERDUE") return `已超过处理时限 · 原定 ${dueAt} 前处理`;
  if (item.slaStatus === "DUE_SOON") return `即将到处理时限 · 请在 ${dueAt} 前处理`;
  return `处理时限：${dueAt} 前`;
}

export function serviceRequestEscalationPresentation(
  item: ServiceRequestWorkItemReadModel,
): { label: string; reason: string | null } | null {
  if (item.escalationStatus === "MANAGER_NOTIFIED") {
    return { label: "已通知店长协助安排", reason: item.escalationReason };
  }
  if (item.escalationStatus === "OWNER_NOTIFIED") {
    return { label: "已通知老板协助安排", reason: item.escalationReason };
  }
  return null;
}
